import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, Skeleton, tableCellClasses } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import PopupMap from '/@/components/ask-an-expert/popup-map';
import MobileFieldStepper from '/@/components/crop-monitoring/mobile-field-stepper';
import Drawing from '/@/components/drawing';
import Icon from '/@/components/icon';
import Card from '/@/components/new-home/card';
import colors from '/@/components/theme-provider/colors.js';
import settings from '/@/core/settings';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { useMyFields } from '/@/hooks/fields';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { setSelectedFeature } from '/@/store/nav/actions';
import { getSessionCountry } from '/@/store/session/selectors';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import Input from '/@/v3/components/forms/input';
import Title from '/@/v3/components/title';
import NewFieldGrowersModal from '/@/views/dashboard/growers/components/new-field-modal';
import styles from '../styles.module.css';

const headCells = [
    {
        id: 'name',
        label: 'Nombre',
    },
    {
        id: 'location',
        label: 'Ubicación',
    },
    {
        id: 'blankSpace',
        label: '',
    },
];

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead sx={{ '& tr th': { border: 0, textAlign: 'left' } }}>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align='center'
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12 }}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                            sx={{
                                color: 'text.accent',
                                '&.Mui-active': {
                                    color: 'text.accent',
                                },
                            }}
                        >
                            <Title bolder size='sm' theme={settings.app.theme}>
                                {_(headCell.label)}
                            </Title>

                            {orderBy === headCell.id ? (
                                <Box component='span' sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const CreateFieldStepper = ({ onClick, selected, disablePointFields = false, selectedFeature, edit = false }) => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();
    const [order, setOrder] = useState(disablePointFields ? 'length' : 'asc');
    const [orderBy, setOrderBy] = useState('name');
    const country = useSelector(getSessionCountry);
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 1000);
    const [page, setPage] = useState(0);
    const [field, setField] = useState(undefined);
    const setSelectedFeatureDispatch = bindActionCreators(setSelectedFeature, dispatch);
    const [checkbox, setCheckBox] = useState({
        poligons: true,
        points: true,
    });
    const fieldsQuery = useMyFields(
        {
            page: page + 1,
            pageSize: 5,
            geometryType: checkbox.poligons == checkbox.points ? undefined : checkbox.poligons ? 'polygon' : 'point',
            country: country.code,
            name: debouncedSearchText,
            ordering: ((order === 'desc' ? '-' : '') + orderBy) as 'name' | '-name' | 'location' | '-location',
        },
        {
            enabled: !isMobile,
        }
    );

    // New field
    const [searchParams] = useSearchParams();
    const userNodeId = searchParams.get('userNodeId');
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const ownNodeQuery = useOwnUserNode();

    // Reset to page 0 when change filters/sort
    useEffect(() => {
        setPage(0);
    }, [debouncedSearchText, checkbox.poligons, checkbox.points, order, orderBy]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleClickCard = () => {
        if (settings.app.theme === 'expertobayer') {
            setNewFieldModalOpen(true);
        } else {
            history(reverse('fields.NewField'));
            setSelectedFeatureDispatch({ view: 'fields.NewField', selectedFeature: selectedFeature });
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChange = (event) => {
        setCheckBox({
            ...checkbox,
            [event.target.name]: event.target.checked,
        });
    };

    const id = open ? 'simple-popover' : undefined;

    return (
        <Fragment>
            {isMobile ? (
                <MobileFieldStepper
                    selected={selected}
                    onClick={onClick}
                    disablePointFields={disablePointFields}
                    checkbox={checkbox}
                    handleChange={handleChange}
                    selectedFeature={selectedFeature}
                />
            ) : (
                <Fragment>
                    <Grid container justifyContent='space-between' spacing={2} sx={{ mt: 3 }} alignItems='center'>
                        <Grid item xs={6}>
                            <Input
                                color={settings.app.theme}
                                value={searchText}
                                onChange={(text) => setSearchText(text)}
                                placeholder={_('Buscar por Nombre, Ubicación...')}
                            />
                        </Grid>
                        {!edit && (
                            <Grid item xs={3}>
                                <Card
                                    icon='plus'
                                    iconElement={<Icon type='newField' className={styles.iconCardNewField} />}
                                    name={_('Crear un nuevo lote')}
                                    direction='row'
                                    color={settings.app.theme === 'expertobayer' ? 'expertobayer' : 'green-light'}
                                    bolder
                                    shadow={false}
                                    center
                                    maxWidth
                                    handleClick={handleClickCard}
                                    border={false}
                                />
                            </Grid>
                        )}
                        {disablePointFields && (
                            <Grid item xs={3}>
                                <Button
                                    onClick={handleClick}
                                    variant='contained'
                                    endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                >
                                    {_('Mostrar lotes')}
                                </Button>
                                <Popover
                                    id={id}
                                    open={open}
                                    anchorEl={anchorEl}
                                    onClose={handleClose}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    }}
                                    PaperProps={{ style: { padding: 15, display: 'flex', flexDirection: 'column' } }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                defaultChecked
                                                onChange={(e) => {
                                                    handleChange(e);
                                                }}
                                                name='poligons'
                                                checked={checkbox.poligons}
                                            />
                                        }
                                        label={_('Dibujados con polígonos (válidos)')}
                                        sx={{
                                            fontSize: 14,
                                            color: (t) => t.palette.primary.main,
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                defaultChecked
                                                onChange={(e) => {
                                                    handleChange(e);
                                                }}
                                                name='points'
                                                checked={checkbox.points}
                                            />
                                        }
                                        label={_('Marcados con un punto (No válidos)')}
                                        sx={{
                                            fontSize: 14,
                                            color: (t) => t.palette.primary.main,
                                        }}
                                    />
                                </Popover>
                            </Grid>
                        )}
                    </Grid>
                    <PopupMap
                        visible={Boolean(field)}
                        onClose={() => setField(undefined)}
                        fields={[field]}
                        zoomPosition='BOTTOM_RIGHT'
                        country={country}
                        field={field}
                    />
                    {
                        <TableContainer sx={{ width: '100%', boxShadow: 'none' }} component={Paper}>
                            <Table
                                sx={{ minWidth: 500, borderCollapse: 'separate', borderSpacing: '0 10px' }}
                                aria-label='simple table'
                            >
                                {Boolean(!fieldsQuery.data && !fieldsQuery.isValidating) && (
                                    <Grid sx={{ py: 6 }}>
                                        <Drawing
                                            type='library-not-found'
                                            title={_('No se encontraron resultados para tu búsqueda.')}
                                        />
                                    </Grid>
                                )}

                                <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />

                                <TableBody>
                                    {fieldsQuery.isValidating &&
                                        [...Array(5)].map((_, i) => (
                                            <TableRow
                                                sx={{
                                                    td: {
                                                        border: '1px solid #E1E1E1',
                                                        borderLeft: 0,
                                                        borderRight: 0,
                                                    },
                                                    'td:first-child': {
                                                        borderLeft: '1px solid #E1E1E1',
                                                        borderTopLeftRadius: '8px',
                                                        borderBottomLeftRadius: '8px',
                                                        borderTopRightRadius: 0,
                                                        borderBottomRightRadius: 0,
                                                        borderRight: 0,
                                                    },
                                                    'td:last-child': {
                                                        borderLeft: 0,
                                                        borderRight: '1px solid #E1E1E1',
                                                        borderTopLeftRadius: 0,
                                                        borderBottomLeftRadius: 0,
                                                        borderTopRightRadius: '8px',
                                                        borderBottomRightRadius: '8px',
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    sx={{
                                                        width: '20%',
                                                    }}
                                                    scope='row'
                                                >
                                                    <Skeleton height='40px' />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '40%',
                                                    }}
                                                    scope='row'
                                                >
                                                    <Skeleton height='40px' />
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        width: '40%',
                                                        '@media(maxWidth: 1000px)': {
                                                            width: 'maxContent',
                                                        },
                                                    }}
                                                    scope='row'
                                                >
                                                    <Skeleton height='40px' />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    {!fieldsQuery.isValidating &&
                                        (fieldsQuery.data?.results || []).map((field) => {
                                            return (
                                                <Tooltip
                                                    placement='bottom'
                                                    disableHoverListener={
                                                        !disablePointFields ||
                                                        (disablePointFields && field.geometry.length > 1)
                                                    }
                                                    title={
                                                        disablePointFields &&
                                                        field.geometry.length === 1 && (
                                                            <Grid container direction='column' sx={{ gap: 1, p: 2 }}>
                                                                <Grid item>
                                                                    <Title color='white' bolder size='md'>
                                                                        {_('Lote no compatible para N-Smart')}
                                                                    </Title>
                                                                </Grid>
                                                                <Grid item>
                                                                    <span>
                                                                        {_('Este lote fue creado con la opción ')}
                                                                    </span>
                                                                    <span style={{ fontWeight: 800 }}>
                                                                        {_('"Marcar un punto en el Mapa."')}
                                                                    </span>
                                                                    <span>
                                                                        {_(
                                                                            'Para una recomendación N-Smart debemos saber el área del mismo, por esto, debe ser creado con la opción '
                                                                        )}
                                                                    </span>
                                                                    <span style={{ fontWeight: 800 }}>
                                                                        {_('"Dibujar polígono".')}
                                                                    </span>
                                                                </Grid>
                                                            </Grid>
                                                        )
                                                    }
                                                >
                                                    <TableRow
                                                        key={_(field.id)}
                                                        sx={{
                                                            fontSize: 14,
                                                            td: {
                                                                border: '1px solid #E1E1E1',
                                                                borderLeft: 0,
                                                                borderRight: 0,
                                                            },
                                                            'td:first-child': {
                                                                borderLeft: '1px solid #E1E1E1',
                                                                borderTopLeftRadius: '8px',
                                                                borderBottomLeftRadius: '8px',
                                                                borderTopRightRadius: 0,
                                                                borderBottomRightRadius: 0,
                                                                borderRight: 0,
                                                            },
                                                            'td:last-child': {
                                                                borderLeft: 0,
                                                                borderRight: '1px solid #E1E1E1',
                                                                borderTopLeftRadius: 0,
                                                                borderBottomLeftRadius: 0,
                                                                borderTopRightRadius: '8px',
                                                                borderBottomRightRadius: '8px',
                                                            },
                                                            ...(field.id == selected && {
                                                                [`.${tableCellClasses.root}`]: {
                                                                    backgroundColor:
                                                                        colors.themes[settings.app.theme].primary[300],
                                                                },
                                                            }),
                                                            ...(field.id !== selected &&
                                                                !disablePointFields &&
                                                                !field.geometry.length === 1 && {
                                                                    [`&:hover .${tableCellClasses.root}`]: {
                                                                        backgroundColor:
                                                                            colors.themes[settings.app.theme]
                                                                                .primary[100],
                                                                    },
                                                                }),
                                                            ...(disablePointFields &&
                                                                field.geometry.length === 1 && {
                                                                    [`.${tableCellClasses.root}`]: {
                                                                        backgroundColor: colors.gray[100],
                                                                    },
                                                                }),
                                                        }}
                                                    >
                                                        <TableCell
                                                            sx={{
                                                                width: '20%',
                                                                ...(disablePointFields &&
                                                                    field.geometry.length === 1 && {
                                                                        color: colors.gray[800],
                                                                    }),
                                                            }}
                                                            scope='row'
                                                            className='mp-mask'
                                                        >
                                                            {field.name}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                width: '40%',
                                                                ...(disablePointFields &&
                                                                    field.geometry.length === 1 && {
                                                                        color: colors.gray[800],
                                                                    }),
                                                            }}
                                                            scope='row'
                                                        >
                                                            {field.location}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={{
                                                                width: '40%',
                                                                '@media(maxWidth: 1000px)': {
                                                                    width: 'maxContent',
                                                                },
                                                            }}
                                                            scope='row'
                                                        >
                                                            <Grid container spacing={2} justifyContent='end'>
                                                                <Grid item>
                                                                    <Button
                                                                        onClick={() => setField(field)}
                                                                        variant='contained'
                                                                        fullWidth
                                                                        disabled={
                                                                            disablePointFields &&
                                                                            field.geometry.length === 1
                                                                        }
                                                                    >
                                                                        {_('Ver en el mapa')}
                                                                    </Button>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Button
                                                                        fullWidth
                                                                        onClick={() => onClick(field.id)}
                                                                        variant='contained'
                                                                        disabled={
                                                                            disablePointFields &&
                                                                            field.geometry.length === 1
                                                                        }
                                                                    >
                                                                        {_('Seleccionar')}
                                                                    </Button>
                                                                </Grid>
                                                            </Grid>
                                                        </TableCell>
                                                    </TableRow>
                                                </Tooltip>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }

                    <Pagination
                        size='small'
                        shape='rounded'
                        count={Math.ceil(fieldsQuery.data?.count / 5)}
                        defaultPage={1}
                        page={page + 1}
                        onChange={(ev, page) => handleChangePage(ev, page - 1)}
                        color='primary'
                        sx={{
                            p: 3,
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    />
                </Fragment>
            )}
            {ownNodeQuery.data?.id && (
                <NewFieldGrowersModal
                    open={newFieldModalOpen}
                    setOpen={setNewFieldModalOpen}
                    growerUserNodeId={userNodeId ? Number(userNodeId) : ownNodeQuery.data?.id}
                    trackEntrySource={
                        selectedFeature === 'newNitrogenRecommendationForm'
                            ? 'n-smart-recommendation'
                            : 'crop-monitoring'
                    }
                />
            )}
        </Fragment>
    );
};

export default CreateFieldStepper;
